import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import fetchFromAPI from "../utils/fetchFromAPI";
import { formatDeadline } from "../utils/deadline";
import "./SubmissionGuidelinesModal.css";

const CLOSE_ANIMATION_MS = 220;

const SubmissionGuidelinesModal = ({ onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [rules, setRules] = useState(null);
  const [rulesFailed, setRulesFailed] = useState(false);

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(onClose, CLOSE_ANIMATION_MS);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await fetchFromAPI("/api/rules");
        setRules(response.data);
      } catch (error) {
        console.error("Error fetching rules:", error);
        setRules(null);
        setRulesFailed(true);
      }
    };

    fetchRules();
  }, []);

  return (
    <div
      className={`guidelines-modal-overlay${isClosing ? " is-closing" : ""}`}
      onClick={handleClose}
    >
      <div
        className={`guidelines-modal${isClosing ? " is-closing" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guidelines-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          className="guidelines-modal-close"
          onClick={handleClose}
          aria-label="Close guidelines"
        >
          <X size={22} />
        </button>

        <div className="guidelines-modal-header">
          <h2 className="guidelines-modal-brand">Short Cuts</h2>
          <h1 id="guidelines-modal-title" className="guidelines-modal-title">
            Film Submission
            <br />
            Guidelines
          </h1>
        </div>

        {rules && (rules.form || rules.wildcard) && (
          <div className="guidelines-modal-rules">
            {rules.form && (
              <div className="guidelines-modal-rule">
                <div className="guidelines-modal-rule-label">Form</div>
                <div className="guidelines-modal-rule-text">{rules.form}</div>
              </div>
            )}
            {rules.wildcard && (
              <div className="guidelines-modal-rule">
                <div className="guidelines-modal-rule-label">Wildcard</div>
                <div className="guidelines-modal-rule-text">
                  {rules.wildcard}
                </div>
              </div>
            )}
            {formatDeadline(rules.due_date) && (
              <div className="guidelines-modal-rule guidelines-modal-deadline">
                <div className="guidelines-modal-rule-label">Deadline</div>
                <div className="guidelines-modal-rule-text">
                  {formatDeadline(rules.due_date)}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="guidelines-modal-columns">
          <ul className="guidelines-modal-list">
            <li>
              The film <em>must</em> have been made during the film challenge
              period, for the film challenge
            </li>
            <li>
              The director <em>must</em> be present at the screening
            </li>
            <li>
              The final cut of the film <em>must</em> be submitted by the
              challenge deadline
            </li>
            <li>
              Films <em>under 5 minutes</em> are heavily prioritized
            </li>
          </ul>

          <ul className="guidelines-modal-list">
            <li>
              The film <em>must</em> follow the challenge's rules; both Form
              and Wildcard
            </li>
            <li>
              The film <em>must not</em> be made primarily with generative AI
            </li>
            <li>
              <em>All</em> roles <em>must</em> be credited on the film
              submission page
            </li>
            <li>
              We reserve the right to decline screening <em>any</em> film for{" "}
              <em>any</em> reason, including time constraints or content
              considerations
            </li>
          </ul>
        </div>

        <button
          className="guidelines-modal-acknowledge"
          onClick={handleClose}
          disabled={!rules && !rulesFailed}
        >
          I Understand
        </button>
      </div>
    </div>
  );
};

export default SubmissionGuidelinesModal;
